import { Being } from "./Being";
import { Beast } from "./Beast";
import type { IGameLevel } from "../levels/IGameLevel";

export enum ToolType {
    Raise,
    Lower,
    Flatten,
    Meteor,
    Prophet,
    Revelation,
    SpawnBeast
}

/** Holds all mutable world state: terrain heights, beings, active tool, and current level. */
export class WorldModel {
    private _terrainHeights: Float32Array;
    private _terrainSize: number;
    private _subdivisions: number;
    private _currentTool: ToolType;
    private _beings: Being[] = [];
    private _beasts: Beast[] = [];
    private _level!: IGameLevel;

    /** Latched once the game-over event has fired, so it only fires a single time. */
    public gameOverFired = false;

    /** The currently active terrain-editing tool. */
    public get currentTool(): ToolType {
        return this._currentTool;
    }

    public set currentTool(tool: ToolType) {
        this._currentTool = tool;
    }

    public get level(): IGameLevel {
        return this._level;
    }

    public set level(level: IGameLevel) {
        this._level = level;
    }

    /**
     * @param terrainSize - Side length of the square terrain in world units.
     * @param subdivisions - Number of grid subdivisions along each axis.
     * @param level - The initial game level.
     */
    constructor(terrainSize: number, subdivisions: number, level: IGameLevel) {
        this._terrainSize = terrainSize;
        this._subdivisions = subdivisions;
        this._terrainHeights = new Float32Array((subdivisions + 1) * (subdivisions + 1));
        this._currentTool = ToolType.Raise;
        this._level = level;
    }

    /** Appends `beings` to the world's being list. */
    public addBeings(beings: Being[]): void {
        this._beings.push(...beings);
    }

    /** All beings currently in the world. */
    public get beings(): Being[] {
        return this._beings;
    }

    /** Appends `beasts` to the world's beast list. */
    public addBeasts(beasts: Beast[]): void {
        this._beasts.push(...beasts);
    }

    /** All beasts currently in the world. */
    public get beasts(): Beast[] {
        return this._beasts;
    }

    /** Removes all beasts whose health has reached zero. */
    public removeDeadBeasts(): void {
        this._beasts = this._beasts.filter(b => b.health > 0);
    }

    /** Flat array of terrain vertex heights in row-major order. */
    public get terrainHeights(): Float32Array {
        return this._terrainHeights;
    }

    /** Side length of the square terrain in world units. */
    public get terrainSize(): number {
        return this._terrainSize;
    }

    /** Number of grid subdivisions along each axis. */
    public get subdivisions(): number {
        return this._subdivisions;
    }

    /**
     * Overwrites the internal terrain heights with `updatedHeights`.
     * Logs an error and returns without mutating if the array lengths differ.
     */
    public updateTerrainHeights(updatedHeights: Float32Array): void {
        if (updatedHeights.length !== this._terrainHeights.length) {
            console.error("Mismatched array length for terrain heights update.");
            return;
        }
        this._terrainHeights.set(updatedHeights);
    }
}
